import { NoTranslateCell } from "./types";
import { useCallback, useEffect, useMemo, useState } from "react";

type getCellReturns = {
  cell: { column: number; row: number };
  mark: (locked?: boolean) => void;
  unmark: (allowedToRemoveLocked?: boolean) => void;
  info: { locked?: boolean };
  marked: boolean;
};
type getRowReturns = {
  cells: getCellReturns[];
  mark: (locked?: boolean) => void;
  unmark: () => void;
  info: { locked?: boolean };
  marked: boolean;
};

type getColumnReturns = {
  cells: getCellReturns[];
  mark: (locked?: boolean) => void;
  unmark: () => void;
  info: { locked?: boolean };
  marked: boolean;
};

type useNoTranslateCellsReturns = {
  cells: NoTranslateCell[];
  getCell: (row: number, column: number) => getCellReturns | undefined;
  getRow: (row: number) => getRowReturns;
  getColumn: (column: number) => getColumnReturns;
  markCellAsNoTranslateInFile: (indexes: {
    row: number;
    column: number;
  }) => void;
};

export const useNoTranslateCells = (
  rows: number,
  columns: number
): useNoTranslateCellsReturns => {
  const [cells, setCells] = useState<{ column: number; row: number }[]>([]);
  const [noTranslateIndexes, setNoTranslateIndexes] = useState<
    Record<number, { locked?: boolean }>
  >({});

  const [
    cellIndexesIsNoTranslateInFile,
    setCellIndexesIsNoTranslateInFile,
  ] = useState<number[]>([]);

  useEffect(() => {
    setCells([]);
    setNoTranslateIndexes({});
    for (let row = 1; row <= rows; row++) {
      for (let column = 1; column <= columns; column++) {
        setCells((prev) => [...prev, { row, column }]);
      }
    }
  }, [rows, columns]);

  const cellIsHardNoTranslate = useCallback(
    (index: number) => {
      return cellIndexesIsNoTranslateInFile.includes(index);
    },
    [cellIndexesIsNoTranslateInFile]
  );

  const markCellAsNoTranslate = (
    index: number,
    locked: boolean = false
  ): void => {
    if (cellIsHardNoTranslate(index)) {
      return;
    }
    const wasLocked = noTranslateIndexes?.[index]?.locked;
    setNoTranslateIndexes((prev) => ({
      ...prev,
      [index]: { locked: locked || wasLocked },
    }));
  };
  const unmarkCellAsNoTranslate = useCallback(
    (index: number, allowedToRemoveLocked: boolean = false): void => {
      if (cellIsHardNoTranslate(index)) {
        return;
      }
      setNoTranslateIndexes((prev) => {
        const newState = { ...prev };
        const cell = newState?.[index];
        if (cell && (!cell.locked || allowedToRemoveLocked)) {
          delete newState[index];
        }
        return newState;
      });
    },
    [cellIsHardNoTranslate]
  );

  const getCell = useCallback(
    (row: number, column: number): getCellReturns | undefined => {
      const cellIndex = cells.findIndex(
        (cell) => cell.row === row && cell.column === column
      );
      if (cellIndex === -1) {
        return undefined;
      }
      const cell = cells[cellIndex];
      if (cell) {
        return {
          cell,
          mark: (locked?: boolean) => markCellAsNoTranslate(cellIndex, locked),
          unmark: (allowedToRemoveLocked?: boolean) =>
            unmarkCellAsNoTranslate(cellIndex, allowedToRemoveLocked),
          info: noTranslateIndexes?.[cellIndex],
          marked: !!noTranslateIndexes?.[cellIndex],
        };
      }
      return undefined;
    },
    [cells, noTranslateIndexes]
  );

  const getCellsInRow = useCallback(
    (row: number): getCellReturns[] => {
      return cells
        .filter((cell) => cell.row === row)
        .map((cell) => getCell(cell.row, cell.column));
    },
    [cells, getCell]
  );

  const markRowAsNoTranslate = useCallback(
    (cells: getCellReturns[], locked: boolean = false): void => {
      cells.forEach((cell) => {
        cell.mark(locked);
      });
    },
    [getCellsInRow]
  );

  const unmarkRowAsNoTranslate = useCallback(
    (cells: getCellReturns[]): void => {
      cells.forEach((cell) => {
        cell.unmark();
      });
    },
    [getCellsInRow]
  );

  const getCellsInColumn = useCallback(
    (column: number): getCellReturns[] => {
      return cells
        .filter((cell) => cell.column === column)
        .map((cell) => getCell(cell.row, cell.column));
    },
    [cells, getCell]
  );

  const markColumnAsNoTranslate = useCallback(
    (cells: getCellReturns[], locked: boolean = false): void => {
      cells.forEach((cell) => {
        cell.mark(locked);
      });
    },
    [getCellsInColumn]
  );

  const unmarkColumnAsNoTranslate = useCallback(
    (cells: getCellReturns[]): void => {
      cells.forEach((cell) => {
        cell.unmark(true);
      });
    },
    [getCellsInColumn]
  );

  const getRow = useCallback(
    (row: number): getRowReturns => {
      const cellsInRow = getCellsInRow(row);
      const marked = cellsInRow.every((cell) => cell.marked);
      const locked = cellsInRow.every((cell) => cell.info?.locked);
      return {
        cells: cellsInRow,
        mark: (locked?: boolean) => markRowAsNoTranslate(cellsInRow, locked),
        unmark: () => unmarkRowAsNoTranslate(cellsInRow),
        info: { locked },
        marked,
      };
    },
    [
      cells,
      markRowAsNoTranslate,
      unmarkRowAsNoTranslate,
      noTranslateIndexes,
      getCellsInRow,
    ]
  );

  const getColumn = useCallback(
    (column: number): getColumnReturns => {
      const cellsInColumn = getCellsInColumn(column);
      const marked = cellsInColumn.every((cell) => cell.marked);
      const locked = cellsInColumn.every((cell) => cell.info?.locked);
      return {
        cells: cellsInColumn,
        mark: (locked: boolean = true) =>
          markColumnAsNoTranslate(cellsInColumn, locked),
        unmark: () => unmarkColumnAsNoTranslate(cellsInColumn),
        info: { locked },
        marked,
      };
    },
    [
      cells,
      markColumnAsNoTranslate,
      unmarkColumnAsNoTranslate,
      noTranslateIndexes,
      getCellsInColumn,
    ]
  );

  const cellsMarked = useMemo((): NoTranslateCell[] => {
    return cells
      .filter((cell, index) => noTranslateIndexes?.[index])
      .map((cell, index) => ({
        ...cell,
        ...noTranslateIndexes?.[index],
      }));
  }, [cells, noTranslateIndexes]);

  const markCellAsNoTranslateInFile = useCallback(
    ({ row, column }: { row: number; column: number }) => {
      const cellIndex = cells.findIndex(
        (cell) => cell.row === row && cell.column === column
      );
      if (cellIndex === -1) {
        return;
      }
      setCellIndexesIsNoTranslateInFile((prev) => [...prev, cellIndex]);
      setNoTranslateIndexes((prev) => ({
        ...prev,
        [cellIndex]: { locked: true },
      }));
    },
    [setCellIndexesIsNoTranslateInFile, setNoTranslateIndexes, cells]
  );

  return {
    cells: cellsMarked,
    getCell,
    getRow,
    getColumn,
    markCellAsNoTranslateInFile,
  };
};
